import { ListBulletIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { Center, rem, SegmentedControl, Select, Group, Tooltip } from "@mantine/core";
import { SVGProps } from "react";
import { useTranslation } from "../../../hooks/useTranslation";

const IconSquares2X2 = (props: SVGProps<SVGSVGElement>) => (
    <Squares2X2Icon {...props} />
);
const IconListBullet = (props: SVGProps<SVGSVGElement>) => (
    <ListBulletIcon {...props} />
);

type ViewMode = "grid" | "list";

interface EmployeeOverviewToolbarProps {
    pageSize: number;
    onPageSizeChange: (value: string) => void;
    viewMode: ViewMode;
    onViewModeChange: (value: string) => void;
}

/**
 * Consolidates the page-size select + view toggle into one shared component instead of
 * duplicating it inside both the grid and table views (the pattern CVListGrid/CVListTable
 * each do independently) — rendered once by the orchestrator above whichever view is active.
 */
export function EmployeeOverviewToolbar({
    pageSize,
    onPageSizeChange,
    viewMode,
    onViewModeChange,
}: EmployeeOverviewToolbarProps) {
    const { t } = useTranslation();
    return (
        <Group justify="flex-end" gap="xs" mb="sm" wrap="wrap">
            <Select
                size="xs"
                value={pageSize.toString()}
                onChange={(value) => value && onPageSizeChange(value)}
                data={[
                    { value: "5", label: t("EMPLOYEEOVERVIEW_PAGE_SIZE_OPTION", { count: "5" }, "5 per page") },
                    { value: "10", label: t("EMPLOYEEOVERVIEW_PAGE_SIZE_OPTION", { count: "10" }, "10 per page") },
                    { value: "20", label: t("EMPLOYEEOVERVIEW_PAGE_SIZE_OPTION", { count: "20" }, "20 per page") },
                    { value: "50", label: t("EMPLOYEEOVERVIEW_PAGE_SIZE_OPTION", { count: "50" }, "50 per page") },
                ]}
                style={{ minWidth: 110 }}
                aria-label={t("EMPLOYEEOVERVIEW_RESULTS_PER_PAGE_ARIA", undefined, "Results per page")}
            />
            <SegmentedControl
                value={viewMode}
                onChange={onViewModeChange}
                aria-label={t("EMPLOYEEOVERVIEW_VIEW_MODE_ARIA", undefined, "Choose view mode")}
                size="xs"
                color="brand"
                data={[
                    {
                        value: "grid",
                        label: (
                            <Tooltip label={t("EMPLOYEEOVERVIEW_CARD_VIEW_TOOLTIP", undefined, "Card view")} withArrow position="bottom">
                                <Center>
                                    <IconSquares2X2 style={{ width: rem(18), height: rem(18) }} />
                                </Center>
                            </Tooltip>
                        ),
                    },
                    {
                        value: "list",
                        label: (
                            <Tooltip label={t("EMPLOYEEOVERVIEW_LIST_VIEW_TOOLTIP", undefined, "List view")} withArrow position="bottom">
                                <Center>
                                    <IconListBullet style={{ width: rem(18), height: rem(18) }} />
                                </Center>
                            </Tooltip>
                        ),
                    },
                ]}
            />
        </Group>
    );
}
